import {
  CustomPagination,
  DataObjectGroupProcessingStateFilterKey,
  deserializeValueDate,
  ErrorAlertWithDrawer,
  InputDataActiveInfoParams,
  FormattedDate,
  formatDate,
  getIsoDateFromFormattedDate,
  InputDataUploadInfoType,
  ISODate,
  Main,
  Padding,
  serializeValueDate,
  Stack,
  TotalEntries,
  useDirectory,
  useInputDataGroupProcessingStateQuery,
  EMPTY_PAGE,
  deserializeGroupDescription,
  serializeGroupDescription,
  useQueryParams,
  InputProcessingEventsFilterKey,
  Spinner,
  useLoadViewState,
  useSaveDirectoryState,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { Alert, Flex, FormInstance, type PaginationProps } from 'antd';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { InputDataDirectoryTable } from './InputDataDirectoryTable';
import { InputDataHeader } from './InputDataHeader';
import {
  InputDataContextProvider,
  useInputDataStore,
} from './InputDataContext';
import { useRefreshInputDataGroupProcessingStates } from './useRefreshInputDataGroupProcessingStates';
import {
  displayInputDataUploadErrorType,
  useInputDataUploadErrors,
} from './useInputDataUploadErrors';
import { InputDataDirectoryFilter } from './InputDataDirectoryFilter';
import { InputDataUploadInfoModal } from './InputDataUploadInfoModal';
import { InputDataActiveInfoModal } from './InputDataActiveInfoModal';
import { useDaysToSetQueryParamsPeriod } from './utils';
import {
  RelationsTablesModal,
  RelationsTablesModalParams,
} from '../relations/RelationsTablesModal';

const FILTER_KEYS: DataObjectGroupProcessingStateFilterKey[] = [
  'name',
  'userFollowedDataObjectGroups',
  'valueDays',
  'valueDate',
  'onlyLastValueDate',
  'domainId',
  'frequencyId',
  'username',
  'dueDateTime',
  'errorLevelId',
  'dataManager',
  'outputCode',
];

const arrayFields: DataObjectGroupProcessingStateFilterKey[] = [
  'domainId',
  'frequencyId',
  'errorLevelId',
  'valueDate',
  'dueDateTime',
];
const numericFields: DataObjectGroupProcessingStateFilterKey[] = [
  'valueDays',
  'domainId',
  'frequencyId',
  'errorLevelId',
];

const booleanFields: InputProcessingEventsFilterKey[] = [
  'userFollowedDataObjectGroups',
  'onlyLastValueDate',
];

const directoryStateStorageKey = 'inputDataDirectory';

export function InputDataDirectory() {
  useDaysToSetQueryParamsPeriod();
  const [{ onlyLastValueDate, valueDays }] = useQueryParams();

  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    directoryStateStorageKey,
    {
      filters: { onlyLastValueDate: onlyLastValueDate ?? true },
      sort: [{ id: 'valueDate', desc: true }],
    },
  );

  if (!isQueryParamCleared) {
    return null;
  }

  if (valueDays) {
    return <Spinner />;
  }

  return (
    <InputDataContextProvider>
      <InputDataDirectoryContent
        initialFilters={viewState.filters}
        initialSort={viewState.sort}
        initialPagination={viewState.pagination}
      />
    </InputDataContextProvider>
  );
}

interface InputDataDirectoryContentProps {
  initialFilters: Partial<Record<string, any>>;
  initialSort: SortingState;
  initialPagination: PaginationProps | undefined;
}

const InputDataDirectoryContent: FC<InputDataDirectoryContentProps> = (
  props,
) => {
  const filtersRef = useRef<FormInstance>(null);

  const { initialFilters, initialSort, initialPagination } = props;
  const {
    headerProps,
    tableProps,
    paginationProps,
    sidePanelFilterProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialFilters,
    initialSort,
    initialPagination,
    arrayFields,
    numericFields,
    booleanFields,
    sidePanelFilterRef: filtersRef,
    serializeFilter: (...args) => {
      const [[id, value], ...rest] = Object.entries(
        serializeValueDate(args[0], args[1]),
      );
      if (id === 'name') {
        return serializeGroupDescription(id, value);
      }
      return {
        [id]:
          id === 'dueDateTime'
            ? value?.map(
                (d: FormattedDate) =>
                  getIsoDateFromFormattedDate(d) + 'T00:00:00',
              )
            : value,
        ...Object.fromEntries(rest),
      };
    },
    deserializeFilter: (id, value, allQueryFilters) => {
      if (id === 'dueDateTime') {
        return { [id]: value?.map((d: ISODate) => formatDate(d)) };
      } else if (id === 'name' || id === 'userFollowedDataObjectGroups') {
        return deserializeGroupDescription(id, value, allQueryFilters);
      } else {
        return deserializeValueDate(id, value, allQueryFilters);
      }
    },
  });

  useSaveDirectoryState(directoryStateStorageKey, {
    paginationProps,
    queryParameters,
    tableProps,
  });

  const [loadingError, setLoadingError] = useInputDataStore('loadingError');

  const {
    inputDataUploadErrors: uploadErrors,
    setInputDataUploadErrors: setUploadErrors,
  } = useInputDataUploadErrors();
  const [uploadInfo, setUploadInfo] = useState<
    InputDataUploadInfoType | undefined
  >(undefined);
  const [activeInputInfoParams, setActiveInputInfoParams] = useState<
    InputDataActiveInfoParams | undefined
  >(undefined);
  const [relationsTablesParams, setRelationsTablesParams] = useState<
    RelationsTablesModalParams | undefined
  >(undefined);
  const isActionDropdownOpenRef = useRef(false);
  const isAnyModalOpen =
    uploadInfo !== undefined ||
    activeInputInfoParams !== undefined ||
    relationsTablesParams !== undefined;

  const handleShowActiveInputInfo = useCallback(
    (params: InputDataActiveInfoParams) => setActiveInputInfoParams(params),
    [],
  );
  const handleShowRelationsTables = useCallback(
    (params: RelationsTablesModalParams) => setRelationsTablesParams(params),
    [],
  );
  const handleActionDropdownOpenChange = useCallback((open: boolean) => {
    isActionDropdownOpenRef.current = open;
  }, []);
  const isAutoRefreshPaused = useCallback(
    () => isAnyModalOpen || isActionDropdownOpenRef.current,
    [isAnyModalOpen],
  );

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useInputDataGroupProcessingStateQuery(queryParameters, {
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const { data: updatedContent, refresh } =
    useRefreshInputDataGroupProcessingStates(
      content,
      queryParameters.dataManager,
      {
        isAutoRefreshPaused,
      },
    );

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <Main>
      <Main.Col border={false}>
        {loadingError && (
          <Alert
            banner
            type="error"
            message={loadingError}
            onClose={() => setLoadingError(undefined)}
            closable
          />
        )}
        <Main.Content padding={false} id="input-data-directory-content">
          {uploadErrors.size > 0 && (
            <ErrorAlertWithDrawer
              validationMessagesMap={uploadErrors}
              renderErrorHeading={displayInputDataUploadErrorType}
              onClose={() => setUploadErrors([])}
              closable
            />
          )}
          {uploadInfo && (
            <InputDataUploadInfoModal
              uploadInfo={uploadInfo}
              onClose={() => setUploadInfo(undefined)}
            />
          )}
          {activeInputInfoParams && (
            <InputDataActiveInfoModal
              params={activeInputInfoParams}
              onClose={() => setActiveInputInfoParams(undefined)}
            />
          )}
          {relationsTablesParams && (
            <RelationsTablesModal
              params={relationsTablesParams}
              onClose={() => {
                setRelationsTablesParams(undefined);
              }}
            />
          )}
          <DirectoryWrapper size="md">
            <Padding top horizontal>
              <InputDataHeader
                {...headerProps}
                infoIconUrl={InfoIconUrl.INPUT_DATA_DIRECTORY}
                valueDate={queryParameters.valueDate}
                onFilterToggle={() => setIsFilterOpen((prev) => !prev)}
                isFilterOpen={isFilterOpen}
                onRefresh={refetch}
                upload={{
                  onUpload: (response) => setUploadInfo(response),
                  onUploadErrors: (errors) => setUploadErrors(errors),
                }}
              />
            </Padding>
            <Padding bottom horizontal>
              <Flex vertical gap="small">
                <InputDataDirectoryTable
                  data={updatedContent || []}
                  isLoading={isFetching}
                  onShowActiveInputInfo={handleShowActiveInputInfo}
                  onActionSuccess={refresh}
                  onShowRelationsTables={handleShowRelationsTables}
                  onActionDropdownOpenChange={handleActionDropdownOpenChange}
                  {...tableProps}
                />
              </Flex>
            </Padding>
          </DirectoryWrapper>
        </Main.Content>
        <Main.Footer>
          <TotalEntries count={totalElements} />
          <FooterPagination {...paginationProps} />
        </Main.Footer>
      </Main.Col>
      {isFilterOpen && (
        <InputDataDirectoryFilter
          ref={filtersRef}
          onClose={() => setIsFilterOpen(false)}
          dataManager={queryParameters.dataManager}
          outputCode={queryParameters.outputCode}
          {...sidePanelFilterProps}
        />
      )}
    </Main>
  );
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const DirectoryWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
